import { useService } from '@aesop-fables/containr-react';
import { Typography, Card, Grid } from '@mui/material';
import { useState, useEffect, useRef } from 'react';
import { Modal, Spacer } from '../components';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Authentication, Data, Hooks } from '@3nickels/data-modules';
import { API_BASE_URL } from '../configuration/Environment';
import Cookies from 'js-cookie';
import { t } from 'i18next';
import { handleLogout as globalHandleLogout } from '../utils/logoutHandler';

const formatRemainingTime = (milliseconds: number) => {
  const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
    2,
    '0'
  )}:${String(seconds).padStart(2, '0')}`;
};

const SessionTimeoutModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);
  const commands = Hooks.useCommands();

  const autoLogoutService = useService<Authentication.AutoLogoutService>(
    Authentication.AuthenticationServices.AutoLogoutService
  );
  const authContext = useService<Authentication.WebSessionAuthenticationContext>(
    Authentication.AuthenticationServices.Context
  );

  useEffect(() => {
    const checkInterval = setInterval(() => {
      setTimeRemaining(autoLogoutService.getTimeUntilLogout());
      const shouldBeOpen = autoLogoutService.isInWarningPeriod();
      if (shouldBeOpen !== open) {
        setOpen(shouldBeOpen);
      }
    }, 1000);
    return () => clearInterval(checkInterval);
  }, [autoLogoutService, open]);

  const handleKeepWorking = async (): Promise<void> => {
    await commands.relay(Data.People.Commands.ReloadAccount);
    setOpen(false);
  };

  const handleLogout = (): void => {
    globalHandleLogout(() => {
      setOpen(false);
      authContext.setIsAuthenticated(false);
      formRef.current?.submit();
    });
  };

  return (
    <>
      <form
        ref={formRef}
        method='post'
        action={`${API_BASE_URL}/logout`}
        style={{ display: 'none' }}>
        <input type='hidden' name='_csrf' value={Cookies.get('XSRF-TOKEN')} readOnly />
      </form>
      <Modal
        title={t('AreYouStillWorking')}
        titleStyle={{ color: 'primary.main' }}
        primaryButtonText={t('KeepWorking')}
        secondaryButtonText={t('SignOut')}
        open={open}
        setOpen={setOpen}
        handleSave={handleKeepWorking}
        handleCancel={handleLogout}
        onClose={handleKeepWorking}>
        <Grid display='flex' flex={1} flexDirection='column' width='500px'>
          <Typography variant='p14' color='secondary'>
            {t('YourSessionIsAboutToExpire')}
          </Typography>
          <Spacer height='sm' />
          <Card variant='innerCard'>
            <Grid display='flex' flex={1} p={2.5} columnGap={1} alignItems='center'>
              <AccessTimeIcon />
              <Grid display='flex' flex={1} alignItems='center' justifyContent='space-between'>
                <Typography variant='p16'>{t('TimeBeforeAutoLogout')}</Typography>
                <Typography variant='p18Bold'>{formatRemainingTime(timeRemaining)}</Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Modal>
    </>
  );
};

export default SessionTimeoutModal;
